import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Zap } from 'lucide-react';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';

import { useIterationPreview, useStartIteration } from '../queries/useCommitments';
import styles from './ElementCommitments.module.css';

/** Ile tytułów pokazuje potwierdzenie — reszta jest w liczbie. */
const PREVIEW_LIMIT = 5;

/**
 * „Zacznij iterację" — odcięcie iteracji elementu (spec sek. 7, decyzje 91 i 93).
 *
 * ⭐ Podgląd przed potwierdzeniem nie jest ozdobą: odcięcie bierze też **zaległości**
 * z poprzednich paczek (`<= next`) i zdejmuje status draugra, a **cofnięcia nie ma** —
 * to jest moment, w którym decyzja jest jeszcze tania.
 *
 * ⛔ Widoczność rozstrzyga wołający (właściciel/admin z iteracją do odcięcia) — serwer i tak
 * pilnuje uprawnień (`IElementAccess`), tu chodzi o niepokazywanie zamkniętych drzwi.
 */
export function StartIterationControl({ elementId }: { elementId: number }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const [open, setOpen] = useState(false);
  const preview = useIterationPreview(elementId, open);
  const start = useStartIteration();

  if (!open) {
    return (
      <div className={styles['iteration']}>
        <Button variant="ghost" onClick={() => setOpen(true)}>
          {t('commitments.iteration.start')}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles['iteration']}>
      {preview.isPending ? (
        <Skeleton height="1.5rem" announce />
      ) : preview.isError ? (
        <p role="alert">{describeError(preview.error)}</p>
      ) : (
        <>
          {/* ⛔ Ikona, nie znak czcionki (spec 12B.7). Ten sam `Zap` co przy bezpieczniku
              zamknięcia — obie rzeczy zapowiadają OBUDZENIE cudzych zobowiązań. */}
          <p className={styles['iterationHeading']}>
            <Zap className={styles['iterationIcon']} size={14} aria-hidden="true" />
            {t('commitments.iteration.previewHeading', {
              number: preview.data.nextNumber,
              count: preview.data.wouldFire.length,
            })}
          </p>

          {preview.data.wouldFire.length === 0 ? (
            <p className={styles['iterationEmpty']}>{t('commitments.iteration.previewEmpty')}</p>
          ) : (
            <ul className={styles['iterationList']}>
              {preview.data.wouldFire.slice(0, PREVIEW_LIMIT).map((item) => (
                <li key={item.commitmentId}>{item.title}</li>
              ))}
              {preview.data.wouldFire.length > PREVIEW_LIMIT ? (
                <li className={styles['iterationMore']}>
                  {t('commitments.iteration.more', {
                    rest: preview.data.wouldFire.length - PREVIEW_LIMIT,
                  })}
                </li>
              ) : null}
            </ul>
          )}

          <div className={styles['iterationActions']}>
            <Button
              variant="primary"
              disabled={start.isPending || preview.data.wouldFire.length === 0}
              onClick={() =>
                start.mutate(elementId, {
                  onSuccess: (started) => {
                    setOpen(false);
                    show({
                      message: t('commitments.iteration.started', {
                        number: started.number,
                        count: started.fired.length,
                      }),
                      tone: 'success',
                    });
                  },
                  onError: (error) => show({ message: describeError(error), tone: 'danger' }),
                })
              }
            >
              {t('commitments.iteration.confirm')}
            </Button>

            <Button variant="ghost" disabled={start.isPending} onClick={() => setOpen(false)}>
              {t('common.action.cancel')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
